import { MoonIcon, SunIcon } from 'lucide-react'
import { SxProps } from '@mui/material'
import { ToggleButton } from '@repo/ui'

export type Appearance = 'light' | 'dark'

/**
 * The sun/moon pair, defined once.
 *
 * Icon-only, and deliberately so: a sun beside a moon is unambiguous without reading, and
 * every place this control appears is a narrow header where "Light"/"Dark" as text would
 * be a third competing block. `ariaLabel` carries the accessible name, which is why the
 * empty `label` is safe rather than a gap.
 *
 * 15px at `strokeWidth 1.5` — matched to the existing render sites rather than re-chosen,
 * because the whole point of extracting this is that the light/dark switches in the
 * product read as ONE control wherever they turn up.
 */
const APPEARANCE_OPTIONS = [
  { label: '', ariaLabel: 'Light', value: 'light', icon: <SunIcon size={15} strokeWidth={1.5} /> },
  { label: '', ariaLabel: 'Dark', value: 'dark', icon: <MoonIcon size={15} strokeWidth={1.5} /> },
]

interface AppearanceToggleProps {
  value: Appearance
  onChange: (next: Appearance) => void
  sx?: SxProps
}

/**
 * Light/dark selector — the shared `@repo/ui` `ToggleButton` with the sun/moon pair.
 *
 * ## Why this is a component and not two more copies of the options array (§36)
 *
 * The same `APPEARANCE_OPTIONS` literal was already written out TWICE, verbatim, in
 * `settings/CompanyGeneralPage` and `main/StudioPage/components/EmailPreview` — and
 * `CompanyGeneralPage`'s own docblock says so in as many words ("Mirrors
 * `APPEARANCE_OPTIONS` in StudioPage/components/EmailPreview — same control, same glyphs,
 * same sizes"). §36's rule is that one hand-roll is allowed and the SECOND is a shared
 * component; that line was already crossed before the Labs sections needed it. Adding a
 * third and fourth copy here would have made a drifting five-site literal out of a control
 * whose entire job is to look identical everywhere.
 *
 * ⚠️ **Known debt, stated rather than silently left.** The two existing sites are NOT
 * migrated onto this component by this change, because neither file is otherwise touched
 * by VRG-560 and rewriting them would put unreviewed edits into unrelated surfaces (§50).
 * They are `CompanyGeneralPage.tsx:24` and `EmailPreview.tsx:28`, plus
 * `StudioPage/components/LogoUploadCard.tsx`, which renders `ToggleButton` twice with its
 * own option lists. Converge each onto `AppearanceToggle` the next time that surface is
 * touched — that is §36's "migrate it in the same change" clause, deferred to the change
 * that legitimately owns the file.
 *
 * ## A `'light' | 'dark'` value, not a boolean
 *
 * The two existing call sites disagree about what they are driving — `CompanyGeneralPage`
 * drives the app-wide `useColorMode()`, `EmailPreview` drives a local `darkMode` boolean —
 * so this takes the string the control actually deals in and lets each caller adapt. A
 * boolean prop would read `dark={false}` for "light", which is the same value the
 * `useColorMode` caller has to invert twice to use.
 *
 * Clicking the already-active side is a no-op by construction: `ToggleButton` reports the
 * value that was clicked, and callers compare before acting.
 */
export const AppearanceToggle = ({ value, onChange, sx }: AppearanceToggleProps) => (
  <ToggleButton
    tabs={APPEARANCE_OPTIONS}
    tab={value}
    onChange={v => onChange(v === 'dark' ? 'dark' : 'light')}
    sx={{ flexShrink: 0, ...(sx as object) }}
  />
)
